/**
 * slotLayout.js
 * Funções puras de layout de slot compartilhadas entre Calendar.js (desktop)
 * e CalendarMobile.js (mobile).
 *
 * Regras de ouro (não quebrar):
 *  1. Cards bloqueantes (agendado, confirmado, aguardando, em atendimento,
 *     realizado, bloqueado) SEMPRE renderizam e nunca ficam abaixo de uma
 *     largura mínima legível — os non-blockers é que são descartados.
 *  2. A faixa do "+" (STRIP_PX, colada na borda direita) é SEMPRE preservada:
 *     é a área de clique de slot vazio. Nenhum card invade essa faixa.
 *  3. Quando não cabem todos, os non-blockers são descartados do menos
 *     importante para o mais importante: reagendado → cancelado →
 *     cancelado pelo paciente → falta (falta é o último a sair).
 *
 * Sem dependências de React — apenas AppointmentsState (JS puro).
 */

import AppointmentsState from '../../../consts/AppointmentsState';

// ─── Status "não bloqueantes" — apenas efeito visual, não ocupam o slot ───────
// Esses agendamentos existem como registro histórico mas não "bloqueiam" o
// horário. São renderizados como faixa fina à direita, antes do STRIP do "+".
export const NON_BLOCKER_STATUSES = new Set([
  AppointmentsState.Rescheduled,
  AppointmentsState.Canceled,
  AppointmentsState.CanceledByPatient,
  AppointmentsState.Missed,
]);

export function isNonBlocker(status) {
  return NON_BLOCKER_STATUSES.has(status);
}

// ─── Prioridade de status (menor = mais importante) ───────────────────────────
// Bloqueantes: define a coluna mais à esquerda em sobreposições.
// Non-blockers (cauda): define a ordem de empilhamento à direita e a ordem de
// descarte quando não há espaço — o de maior número sai primeiro.
// Ordem da cauda: Falta < Cancelado ≈ Cancelado pelo paciente < Reagendado.
export const STATUS_PRIORITY = {
  [AppointmentsState.Doing]: 0,
  [AppointmentsState.Waiting]: 1,
  [AppointmentsState.Confirmed]: 2,
  [AppointmentsState.ConfirmedByPatient]: 3,
  [AppointmentsState.Scheduled]: 4,
  [AppointmentsState.Done]: 5,
  [AppointmentsState.Blocked]: 6,
  [AppointmentsState.Missed]: 7,
  [AppointmentsState.Canceled]: 8,
  [AppointmentsState.CanceledByPatient]: 9,
  [AppointmentsState.Rescheduled]: 10,
};

export function getStatusPriority(status) {
  return STATUS_PRIORITY[status] ?? 5;
}

// ─── Ordenação determinística dos non-blockers ────────────────────────────────
// Prioridade → hora de início → id. O índice nessa lista é o nbIndex:
// 0 = mais próximo da faixa do "+" (mais importante, último a ser descartado).
export function compareNonBlockers(a, b) {
  const pa = getStatusPriority(a.status);
  const pb = getStatusPriority(b.status);
  if (pa !== pb) return pa - pb;
  const sa = new Date(a.date).getTime();
  const sb = new Date(b.date).getTime();
  if (sa !== sb) return sa - sb;
  return String(a.id != null ? a.id : '').localeCompare(String(b.id != null ? b.id : ''));
}

/**
 * fitNonBlockers(group, columnWidthPx, constants)
 *
 * Decide, por cluster de sobreposição, quais faixas de non-blocker cabem na
 * coluna e quanto espaço os cards bloqueantes precisam reservar à direita.
 *
 * Função PURA: recebe a saída de groupOverlapping() (cada item já traz
 * `nbOverlap` — lista determinística dos non-blockers sobrepostos — e
 * `blockingCount`) e devolve uma nova lista com os campos de ajuste.
 *
 * Campos adicionados a cada item:
 *   nbCount         — total de non-blockers no cluster
 *   nbVisibleCount  — quantos cabem
 *   nbHiddenCount   — quantos foram descartados
 *   nbIndex         — posição do item na lista ordenada (0 = colado no "+")
 *   nbVisible       — se este item (sendo non-blocker) deve renderizar
 *   nbBadgeCount    — > 0 apenas na última faixa visível (badge "+N")
 *   rightReserved   — px reservados à direita dos cards bloqueantes
 *                     (faixa do "+" + faixas de non-blocker que sobreviveram)
 */
export function fitNonBlockers(group, columnWidthPx, constants) {
  if (!group || !group.length) return [];

  const cfg = constants || {};
  const STRIP_PX = cfg.STRIP_PX != null ? cfg.STRIP_PX : 16;
  const NON_BLOCKER_WIDTH_PX = cfg.NON_BLOCKER_WIDTH_PX != null ? cfg.NON_BLOCKER_WIDTH_PX : 28;
  const MIN_BLOCKER_WIDTH_PX = cfg.MIN_BLOCKER_WIDTH_PX != null ? cfg.MIN_BLOCKER_WIDTH_PX : 40;
  const GAP_PX = cfg.GAP_PX != null ? cfg.GAP_PX : 2;

  // Largura útil da coluna. Sem medição confiável, assume 0 → nenhuma faixa
  // de non-blocker (o espaço fica todo com os bloqueantes = comportamento seguro).
  const width = typeof columnWidthPx === 'number' && isFinite(columnWidthPx) && columnWidthPx > 0
    ? columnWidthPx
    : 0;

  const nbSlotPx = NON_BLOCKER_WIDTH_PX + GAP_PX;
  // Faixa do "+" — reservada incondicionalmente, em qualquer cenário.
  const reservedBase = STRIP_PX + GAP_PX;

  return group.map(item => {
    const nbList = item.nbOverlap || [];
    const nbTotal = nbList.length;
    const blockingCount = item.blockingCount || 0;

    // Espaço que sobra para faixas depois do "+" e dos bloqueantes no mínimo legível
    const freeForNb = width - reservedBase - blockingCount * MIN_BLOCKER_WIDTH_PX;
    let nbVisibleCount = nbSlotPx > 0 ? Math.floor(freeForNb / nbSlotPx) : 0;
    if (!isFinite(nbVisibleCount) || nbVisibleCount < 0) nbVisibleCount = 0;
    if (nbVisibleCount > nbTotal) nbVisibleCount = nbTotal;
    // Sem nenhum bloqueante no cluster o espaço pertence aos non-blockers:
    // um cancelado sozinho no slot sempre aparece como faixa fina.
    if (blockingCount === 0 && nbTotal > 0 && nbVisibleCount < 1) nbVisibleCount = 1;

    const nbHiddenCount = nbTotal - nbVisibleCount;
    const rightReserved = reservedBase + nbVisibleCount * nbSlotPx;

    const myIndex = nbList.indexOf(item.appointment);
    const isNb = myIndex >= 0;
    const nbVisible = isNb && myIndex < nbVisibleCount;
    const isLastVisibleNb = nbVisible && myIndex === nbVisibleCount - 1;

    return {
      ...item,
      nbCount: nbTotal,
      nbVisibleCount,
      nbHiddenCount,
      nbIndex: isNb ? myIndex : 0,
      nbVisible,
      nbBadgeCount: isLastVisibleNb && nbHiddenCount > 0 ? nbHiddenCount : 0,
      rightReserved,
    };
  });
}

// Compara duas listas de chaves (número ou texto) posição a posição
function compareKeys(a, b) {
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const x = a[i];
    const y = b[i];
    if (x === y) continue;
    if (typeof x === 'number' && typeof y === 'number') return x - y;
    return String(x ?? '').localeCompare(String(y ?? ''));
  }
  return 0;
}

/**
 * groupOverlapping(appointments, sortKeyOf)
 *
 * Calcula a geometria relativa (colIndex / widthPct / leftPct) de cada
 * agendamento do dia, separando bloqueantes de non-blockers.
 * Idêntico no desktop e no mobile — a decisão de quantas faixas cabem fica
 * por conta de fitNonBlockers(), que recebe a largura real da coluna.
 *
 * sortKeyOf(apt) (opcional) devolve { agenda: [chaves...], name } e fixa a
 * ordem dos cards que começam no mesmo horário: agenda → prioridade de status
 * → nome do paciente → id. Sem ele vale só a prioridade de status, e o
 * desempate é o id — determinístico de qualquer jeito.
 */
export function groupOverlapping(appointments, sortKeyOf = null) {
  if (!appointments || !appointments.length) return [];

  // Filtra apenas os que têm posição no calendário (exclui "disponível")
  const relevant = appointments.filter(
    a => a.status !== AppointmentsState.Available
  );
  if (!relevant.length) return [];

  // Calcula start/end de cada agendamento
  const items = relevant.map(apt => {
    const start = new Date(apt.date).getTime();
    let end = start + 30 * 60 * 1000;
    if (apt.duration) {
      const [dh, dm] = apt.duration.split(':').map(Number);
      end = start + (dh * 60 + dm) * 60 * 1000;
    }
    return { appointment: apt, start, end, key: sortKeyOf ? sortKeyOf(apt) : null };
  });

  // Ordena por hora de início, depois pela agenda (quando há sortKeyOf), depois
  // por prioridade de status (status "bons" pegam colIndex=0 quando há
  // sobreposição na mesma agenda), nome do paciente e id
  const sorted = [...items].sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    const byAgenda = a.key && b.key ? compareKeys(a.key.agenda || [], b.key.agenda || []) : 0;
    if (byAgenda !== 0) return byAgenda;
    const byStatus = getStatusPriority(a.appointment.status) - getStatusPriority(b.appointment.status);
    if (byStatus !== 0) return byStatus;
    const byName = a.key && b.key ? compareKeys([a.key.name || ''], [b.key.name || '']) : 0;
    if (byName !== 0) return byName;
    return compareKeys([String(a.appointment.id ?? '')], [String(b.appointment.id ?? '')]);
  });

  // Atribui colIndex usando sweep de colunas
  const columns = []; // cada coluna guarda o endMs do último item
  const withCol = sorted.map(item => {
    let colIndex = columns.findIndex(colEnd => colEnd <= item.start);
    if (colIndex === -1) {
      colIndex = columns.length;
      columns.push(item.end);
    } else {
      columns[colIndex] = item.end;
    }
    return { ...item, colIndex };
  });

  return withCol.map(item => {
    const overlapping = withCol.filter(
      other => other.start < item.end && other.end > item.start
    );

    // Separa os sobrepostos em bloqueantes e non-blockers
    const normalOverlap = overlapping.filter(o => !isNonBlocker(o.appointment.status));
    // Lista determinística dos non-blockers do cluster: prioridade → início → id.
    // Todos os itens do mesmo cluster derivam o mesmo resultado a partir dela.
    const nbOverlap = overlapping
      .filter(o => isNonBlocker(o.appointment.status))
      .map(o => o.appointment)
      .sort(compareNonBlockers);

    const base = {
      appointment: item.appointment,
      colIndex: item.colIndex,
      colCount: overlapping.length,
      endMs: item.end,
      nbOverlap,
      blockingCount: normalOverlap.length,
    };

    // ── Non-blocker sozinho no slot (sem nenhum bloqueante): faixa à direita ──
    if (normalOverlap.length === 0 && isNonBlocker(item.appointment.status)) {
      return { ...base, widthPct: 100, leftPct: 0, nonBlockerAlone: true };
    }

    // ── Non-blocker sobreposto a bloqueantes: posição fixa à direita ─────────
    if (isNonBlocker(item.appointment.status)) {
      return { ...base, widthPct: 0, leftPct: 0, nonBlockerOverlap: true };
    }

    // ── Agendamento bloqueante sozinho (só ele e, talvez, non-blockers) ──────
    // ── ou em sobreposição com outros bloqueantes ───────────────────────────
    // Divide o espaço apenas entre os bloqueantes (non-blockers ficam fixos à direita)
    const normalSorted = [...normalOverlap].sort((a, b) => a.colIndex - b.colIndex);
    const normalCount = normalSorted.length;
    const myNormalIndex = normalSorted.findIndex(o => o.appointment === item.appointment);
    return {
      ...base,
      widthPct: 100 / normalCount,
      leftPct: (Math.max(myNormalIndex, 0) / normalCount) * 100,
      myNormalIndex: Math.max(myNormalIndex, 0),
      normalCount,
    };
  });
}

/**
 * blockerCardEdges(item)
 * Posição horizontal (CSS left/right) de um card bloqueante.
 * Os bloqueantes dividem em partes iguais a área da coluna MENOS `rightReserved`
 * — assim nunca invadem a faixa do "+" nem as faixas de non-blocker, mesmo
 * quando são muitos e cada um fica mais estreito que o mínimo desejado.
 */
export function blockerCardEdges(item, gapPx = 2) {
  const count = item.normalCount || 1;
  const index = item.myNormalIndex || 0;
  const reserved = item.rightReserved || 0;
  const isLast = index === count - 1;
  const leftFraction = index / count;
  const rightFraction = (count - 1 - index) / count;
  return {
    left: `calc((100% - ${reserved}px) * ${leftFraction} + ${gapPx}px)`,
    right: isLast
      ? `${reserved}px`
      : `calc((100% - ${reserved}px) * ${rightFraction} + ${reserved + gapPx}px)`,
  };
}

/**
 * nonBlockerRightOffset(item, constants)
 * Offset (px) da faixa de non-blocker a partir da borda direita da coluna.
 * nbIndex 0 fica colado na faixa do "+"; os demais empilham para a esquerda.
 */
export function nonBlockerRightOffset(nbIndex, constants) {
  const cfg = constants || {};
  const STRIP_PX = cfg.STRIP_PX != null ? cfg.STRIP_PX : 16;
  const NON_BLOCKER_WIDTH_PX = cfg.NON_BLOCKER_WIDTH_PX != null ? cfg.NON_BLOCKER_WIDTH_PX : 28;
  const GAP_PX = cfg.GAP_PX != null ? cfg.GAP_PX : 2;
  return STRIP_PX + GAP_PX + (nbIndex || 0) * (NON_BLOCKER_WIDTH_PX + GAP_PX);
}
